import { useMutation, useQueryClient } from "@tanstack/react-query";
import { assetsApiRequest } from "../api/api-request";
import { useRequiredParams } from "../utils/url";
import { ApiResponse } from "../api/api-response.model";
import { Point } from "../point/point.model";
import {
  GraphicPictureCategories,
  GraphicCategoryType,
} from "./graphics-category.model";

export const useUploadGraphicsMutation = () => {
  const { companyId, levelId } = useRequiredParams({
    companyId: "number",
    levelId: "number",
  });
  return useMutation({
    mutationFn: (formData: FormData) =>
      uploadGraphics({ companyId, levelId, formData }),
    // TODO: onError
  });
};

export const useDeleteGraphicsMutation = () => {
  return useMutation({
    mutationFn: ({
      graphicsId,
      companyId,
      levelId,
    }: {
      graphicsId: number;
      companyId: number;
      levelId: number;
    }) => deleteGraphics({ companyId, levelId, graphicsId }),
  });
};

const uploadGraphics = ({
  companyId,
  levelId,
  formData,
}: {
  companyId: number;
  levelId: number;
  formData: FormData;
}) => {
  return assetsApiRequest.post(
    `/analytics/companies/${companyId}/levels/${levelId}/graphics`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
};

const deleteGraphics = (params: {
  companyId: number;
  levelId: number;
  graphicsId: number;
}) => {
  return assetsApiRequest
    .delete(
      `analytics/companies/${params.companyId}/levels/${params.levelId}/graphics/${params.graphicsId}`,
    )
    .then((o) => o.data.data);
};

export type UpdateGraphicPointParams = {
  companyId: number;
  graphicsId: number;
  graphic?: string;
  graphicNodeId?: string;
  newPointId?: string;
  pointId?: string;
  pointName?: string;
  deviceId?: string;
};

export function updateScadaGraphicPoint(params: UpdateGraphicPointParams) {
  const { companyId, graphicsId, ...restParams } = params;
  const path = `analytics/companies/${companyId}/graphics/${graphicsId}/points`;
  return assetsApiRequest
    .put<ApiResponse<{ updated: boolean; point: Point }>>(path, restParams)
    .then((o) => o.data.data);
}

export const useUpdateScadaGraphicPointMutation = () => {
  return useMutation({
    mutationFn: updateScadaGraphicPoint,
  });
};

type UpdateScadaGraphicLinkRequest = {
  companyId: number;
  graphicsId: number;
  graphic?: string;
  id: string;
  link: string;
};

export function updateScadaGraphicLink(params: UpdateScadaGraphicLinkRequest) {
  const { id, companyId, graphicsId, ...restParams } = params;
  const path = `analytics/companies/${companyId}/graphics/${graphicsId}/links/${encodeURIComponent(id)}`;
  return assetsApiRequest
    .put<ApiResponse<{ updated: boolean }>>(path, restParams)
    .then((o) => o.data.data);
}

export const useUpdateScadaGraphicLinkMutation = () => {
  return useMutation({
    mutationFn: updateScadaGraphicLink,
  });
};

export type SetGraphicPictureCategoriesParams = {
  companyId: number;
  treeIds: string[];
  categories: GraphicCategoryType[];
};

export function setGraphicPictureCategories(
  params: SetGraphicPictureCategoriesParams,
) {
  const { companyId, treeIds, categories } = params;
  const path = `analytics/companies/${companyId}/graphics/categories`;
  return assetsApiRequest
    .put<ApiResponse<GraphicPictureCategories>>(path, {
      treeIds,
      categories,
    })
    .then((o) => o.data.data.categories);
}

export const useSetGraphicPictureCategoriesMutation = () => {
  const queryClient = useQueryClient();
  const { companyId } = useRequiredParams({ companyId: "number" });

  return useMutation({
    mutationFn: (
      params: Omit<SetGraphicPictureCategoriesParams, "companyId">,
    ) => setGraphicPictureCategories({ ...params, companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tree"],
      });
    },
  });
};
